import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, Linkedin, MessageCircle, Send, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { AnimateIn } from '../components/Layout';
import { profileData } from '../data/mock';
import { useToast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/toaster';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '', company: '', phone: '', email: '', requirement: '',
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.phone || !formData.requirement) {
      toast({ title: "Please fill all required fields", description: "Name, Phone and Requirement are mandatory.", variant: "destructive" });
      return;
    }
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/contact`, formData);
      setIsSubmitted(true);
    } catch (err) {
      toast({ title: "Submission failed", description: "Please try again or contact directly.", variant: "destructive" });
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    { icon: Mail, label: "Email", value: profileData.email, href: `mailto:${profileData.email}` },
    { icon: Phone, label: "Phone", value: profileData.phone, href: `tel:${profileData.phone}` },
    { icon: MapPin, label: "Location", value: profileData.location, href: null },
    { icon: Clock, label: "Response Time", value: profileData.responseTime, href: null },
  ];

  return (
    <div className="pt-20">
      <Toaster />
      {/* Page Hero */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <AnimateIn>
            <span className="text-[#C9A646] font-body font-semibold text-sm tracking-[0.25em] uppercase mb-4 block">Contact</span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-[#0B1F3A] leading-tight mb-6">
              Get In Touch
            </h1>
            <p className="text-gray-500 font-body text-lg max-w-2xl leading-relaxed mb-6">
              Have questions about compliance? Let's discuss your specific requirements.
            </p>
            <div className="w-16 h-[2px] bg-[#C9A646]" />
          </AnimateIn>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-16 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {contactInfo.map((info, idx) => {
              const Icon = info.icon;
              return (
                <AnimateIn key={idx} delay={idx * 100}>
                  <div className="bg-white p-6 rounded-sm text-center hover:shadow-md transition-shadow duration-300">
                    <div className="w-12 h-12 rounded-full bg-[#C9A646]/10 flex items-center justify-center mx-auto mb-4">
                      <Icon size={22} className="text-[#C9A646]" />
                    </div>
                    <p className="text-gray-400 font-body text-sm mb-1">{info.label}</p>
                    {info.href ? (
                      <a href={info.href} className="text-[#0B1F3A] font-body font-semibold hover:text-[#C9A646] transition-colors text-sm">
                        {info.value}
                      </a>
                    ) : (
                      <p className="text-[#0B1F3A] font-body font-semibold text-sm">{info.value}</p>
                    )}
                  </div>
                </AnimateIn>
              );
            })}
          </div>
        </div>
      </section>

      {/* Contact Form & Sidebar */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-16">
            {/* Form */}
            <div className="lg:col-span-3">
              <AnimateIn>
                {isSubmitted ? (
                  <div className="text-center py-16">
                    <div className="w-16 h-16 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-6">
                      <CheckCircle size={32} className="text-green-500" />
                    </div>
                    <h3 className="font-heading text-2xl font-bold text-[#0B1F3A] mb-3">Thank You!</h3>
                    <p className="text-gray-500 font-body text-lg mb-6">
                      Your message has been received. I'll get back to you within 24 hours.
                    </p>
                    <Button
                      onClick={() => {
                        setIsSubmitted(false);
                        setFormData({ name: '', company: '', phone: '', email: '', requirement: '' });
                      }}
                      variant="outline"
                      className="border-2 border-[#0B1F3A] text-[#0B1F3A] hover:bg-[#0B1F3A] hover:text-white font-body rounded-sm"
                    >
                      Send Another Message
                    </Button>
                  </div>
                ) : (
                  <div>
                    <h2 className="font-heading text-2xl md:text-3xl font-bold text-[#0B1F3A] mb-8">Send a Message</h2>
                    <form onSubmit={handleSubmit} className="space-y-6">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                        <div>
                          <label className="text-[#0B1F3A] font-body font-semibold text-sm mb-2 block">Name *</label>
                          <Input name="name" value={formData.name} onChange={handleChange} placeholder="Your full name"
                            className="h-12 rounded-sm border-gray-200 font-body" />
                        </div>
                        <div>
                          <label className="text-[#0B1F3A] font-body font-semibold text-sm mb-2 block">Company</label>
                          <Input name="company" value={formData.company} onChange={handleChange} placeholder="Company name"
                            className="h-12 rounded-sm border-gray-200 font-body" />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                        <div>
                          <label className="text-[#0B1F3A] font-body font-semibold text-sm mb-2 block">Phone *</label>
                          <Input name="phone" value={formData.phone} onChange={handleChange} placeholder="+91 XXXXX XXXXX"
                            className="h-12 rounded-sm border-gray-200 font-body" />
                        </div>
                        <div>
                          <label className="text-[#0B1F3A] font-body font-semibold text-sm mb-2 block">Email</label>
                          <Input name="email" type="email" value={formData.email} onChange={handleChange} placeholder="your@email.com"
                            className="h-12 rounded-sm border-gray-200 font-body" />
                        </div>
                      </div>
                      <div>
                        <label className="text-[#0B1F3A] font-body font-semibold text-sm mb-2 block">Requirement *</label>
                        <Textarea name="requirement" value={formData.requirement} onChange={handleChange}
                          placeholder="Describe your compliance requirements..."
                          className="min-h-[150px] rounded-sm border-gray-200 font-body" />
                      </div>
                      <Button type="submit" disabled={isSubmitting}
                        className="bg-[#C9A646] hover:bg-[#B8953B] text-[#0B1F3A] font-semibold font-body rounded-sm px-8 h-12 disabled:opacity-50">
                        {isSubmitting ? 'Sending...' : 'Send Message'} <Send size={16} className="ml-2" />
                      </Button>
                    </form>
                  </div>
                )}
              </AnimateIn>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-2">
              <AnimateIn delay={200}>
                <div className="bg-[#0B1F3A] p-8 rounded-sm mb-8">
                  <h3 className="font-heading text-xl font-bold text-white mb-4">Prefer a Direct Chat?</h3>
                  <p className="text-gray-400 font-body text-sm leading-relaxed mb-6">
                    Reach out on WhatsApp for a quick response. I typically respond within a few hours.
                  </p>
                  <a href={profileData.whatsapp} target="_blank" rel="noopener noreferrer">
                    <Button className="bg-green-500 hover:bg-green-600 text-white font-body rounded-sm w-full h-11">
                      <MessageCircle size={18} className="mr-2" /> Chat on WhatsApp
                    </Button>
                  </a>
                </div>
                <div className="bg-[#F8F9FA] p-8 rounded-sm">
                  <h3 className="font-heading text-xl font-bold text-[#0B1F3A] mb-4">Connect on LinkedIn</h3>
                  <p className="text-gray-500 font-body text-sm leading-relaxed mb-6">
                    Follow for regular updates on labour law changes and compliance insights.
                  </p>
                  <a href={profileData.linkedin} target="_blank" rel="noopener noreferrer">
                    <Button variant="outline" className="border-2 border-[#0B1F3A] text-[#0B1F3A] hover:bg-[#0B1F3A] hover:text-white font-body rounded-sm w-full h-11">
                      <Linkedin size={18} className="mr-2" /> View LinkedIn Profile
                    </Button>
                  </a>
                </div>
              </AnimateIn>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
